import PropTypes from "prop-types";
import SEPA from "sepa";
// @mui
import { FormGroup, Grid, Switch, Typography, Box, Divider } from "@mui/material";
// components
import { Controller, useForm } from "react-hook-form";
import {
  anredeArray,
  bankDataFields,
  bankDataOwnerField,
  contactDataFields,
  defaultSiteFormValues,
  parentDataFields,
} from "../../../components/form/fields/inputFields";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import DEDateField from "../../../components/form/fields/DEDateField";
import { getDateFromDateField } from "../../../utils/dataUtils";
import IBANTextField from "../../../components/form/fields/IBANTextField";
import { useEffect, useImperativeHandle } from "react";
import DownloadRegistrationPdfButton from "../../../components/download/DownloadRegistrationPdfButton";

// ----------------------------------------------------------------------

export default function EditContractForm({
  member,
  handleEditContract,
  reference,
}) {
  const { contractRef: contractData } = member;
  const { handleSubmit, control, watch, setError, setValue, clearErrors } =
    useForm({
      defaultValues: {
        ...contractData,
      },
    });
  useImperativeHandle(reference, () => ({
    submitForm() {
      handleSubmit(onSubmit)();
    },
  }));

  const isOwner = watch("isOwner");
  const lastName = watch("lastName");
  const firstName = watch("firstName");
  var ownerName = lastName + " " + firstName;

  useEffect(() => {
    if (isOwner === true && member.isChild) {
      setValue("owner", ownerName);
    }
    if (isOwner === true && !member.isChild) {
      setValue("owner", member.lastName + " " + member.firstName);
    }
    if (isOwner === false) {
      setValue("owner", contractData.owner);
    }
  }, [isOwner, setValue, member, contractData, ownerName]);

  const onSubmit = (data) => {
    // Check if an IBAN was typed in and if so if its correct
    if (data.iban) {
      if (!SEPA.validateIBAN(data.iban)) {
        alert("Der eingegebene IBAN ist ungültig, bitte Eingabe prüfen.");
        return;
      }
      data.iban = data.iban.replace(/\s/g, "");
      data.iban = data.iban.toUpperCase();
    }
    handleEditContract({
      ...data,
      birthdate: getDateFromDateField(data?.birthdate),
    });
  };

  const renderSiteInputFields = (fields) => {
    return fields.map((field) => {
      if (field.type === "date") {
        return (
          <Grid
            key={field.value + "-field"}
            item
            xs={12}
            md={field.size ? field.size : 4}
            sx={{ minHeight: 91 }}
          >
            <DEDateField
              control={control}
              name={field.value}
              label={field.label}
              required={field.required}
              errorMsg={field.errorMsg}
              helperText={field.helperText}
            />
          </Grid>
        );
      }

      if (field.value === "iban") {
        return (
          <Grid
            key={field.value + "-field"}
            item
            xs={12}
            md={field.size ? field.size : 4}
            sx={{ minHeight: 91 }}
          >
            <IBANTextField
              control={control}
              name={field.value}
              label={field.label}
              type={field.type}
              variant="outlined"
              required={field.required}
              fullWidth
              setError={setError}
              clearErrors={clearErrors}
            />
          </Grid>
        );
      }

      return (
        <Grid
          key={field.value + "-field"}
          item
          xs={12}
          md={field.size ? field.size : 4}
          sx={field?.minHeight ? { minHeight: 91 } : null}
        >
          <TextField
            control={control}
            name={field.value}
            label={field.label}
            type={field.type}
            variant="outlined"
            required={field.required}
            errorMsg={field.errorMsg}
            helperText={field.helperText}
            fullWidth
            multiline={field?.multiline}
          />
        </Grid>
      );
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ paddingTop: 5 }}
        >
          {/* Registration PDF Download Section */}
          {contractData?.publicRegistrationMeta?.createdViaPublic && contractData?.registrationConfirmationPdf?.relativePath && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Öffentliche Anmeldung
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Dieser Vertrag wurde über das öffentliche Anmeldeformular erstellt.
                </Typography>
                <DownloadRegistrationPdfButton
                  contractId={contractData._id}
                  contract={contractData}
                  variant="button"
                  size="medium"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="h5">Bankdaten</Typography>
          </Grid>
          <Grid item lg={12}>
            <FormGroup row>
              <label style={{ fontSize: "25px" }}>
                Ist {member.isChild ? "der Vertragsnehmer" : "das Mitglied"} der
                Kontoinhaber?
              </label>
              <label
                style={{
                  color: isOwner ? "grey" : "black",
                  marginLeft: 15,
                  marginTop: "5px",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                }}
              >
                Nein
              </label>
              <Controller
                name="isOwner"
                control={control}
                render={({ field }) => (
                  <Switch
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                )}
              />
              <label
                style={{
                  color: isOwner ? "black" : "grey",
                  marginTop: "5px",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                }}
              >
                Ja
              </label>
            </FormGroup>
            <Typography variant="body1">
              Wenn hier "Nein" ausgewählt wird, kann bei Inhaber ein vom
              Vertragsnehmer abweichender Beitragszahler eingegeben werden.
              <br />
              Beispiel: Großeltern zahlen für den Vertrag des Enkelkindes
            </Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ minHeight: 91 }}>
            <TextField
              control={control}
              name={bankDataOwnerField.value}
              label={bankDataOwnerField.label}
              type={bankDataOwnerField.type}
              variant="outlined"
              required={bankDataOwnerField.required}
              fullWidth
              disabled={isOwner}
            />
          </Grid>
          {renderSiteInputFields(bankDataFields)}
          <>
            <Grid item xs={12}>
              <Typography variant="h5">
                Vertragsnehmer/Erziehungsberechtigter
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <SelectField
                style={{ minWidth: 180 }}
                control={control}
                name="title"
                value="title"
                label="Anrede"
                variant="outlined"
                required
                itemArray={anredeArray}
              />
            </Grid>
            {renderSiteInputFields(parentDataFields)}
          </>
          <Grid item xs={12}>
            <Typography variant="h5">Kontaktdaten</Typography>
          </Grid>
          {renderSiteInputFields(contactDataFields)}
        </Grid>
      </form>
    </>
  );
}

EditContractForm.propTypes = {
  siteData: PropTypes.object,
  dialogType: PropTypes.string,
};

EditContractForm.defaultProps = {
  siteData: defaultSiteFormValues, // Hier den Standardwert festlegen
};
